import React from 'react';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import emojione from 'lib/emojione/emojione';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import queryString from 'query-string';
import { replace } from 'lib/emojione/ascii-mapping';
import Styled from 'styled';
import computed from 'lib/mobx/computed';
import { Form, Field } from 'lib/form/form';
import { ArrayOfFieldData } from 'lib/form/array-of-field-data';
import withDialog from 'lib/alerts/with-dialog';
import { RetryDialog } from 'lib/alerts/dialog';
import LinkView from 'lib/ui/link-view';
import { URLBuilderLink } from 'lib/url/url-builder-link';
import SVGIconView from 'lib/images/svg-icon-view';

import FileThumbnailView from 'four-nets/ui/file-thumbnail-view';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import SubmitButtonView from 'four-nets/ui/submit-button/submit-button-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { ValueFieldData, RequiredValidator } from 'lib/form/value-field-data';
import TextAreaView from 'four-nets/ui-v2/form/text-area/text-area';
import PhotoFileInputView from 'four-nets/photofile/form/photofile-input-view';

import FileUploadListView from 'four-nets/ui/form/file-upload-list-view';
import withPhotofiles from 'four-nets/photofile/form/with-photofiles';
import UserMentionable from 'four-nets/user/user-mentionable';
import NewCommentView, { NewCommentViewModel } from 'four-nets/common/comment/new-comment-view';

import defaultStyles from './new-message-view.scss';

@withPhotofiles({ type: 7, fieldName: 'images' })
class NewMessageForm extends Form {}

export { NewMessageViewModel };

@UserMentionable()
class NewMessageViewModel extends NewCommentViewModel {
    form: Form;
    thread: Thread;

    @computed get userMentionableField() {
        return this.form.field.get('text');
    }

    constructor(options) {
        const { thread } = options;

        super({
            user: () => thread.userStore.me,
        });

        this.thread = thread;

        this.setupForm();
    }

    setupForm() {
        this.form = new NewMessageForm({
            photoFileStore: this.thread.photoFileStore,
            field: {
                text: new Field({
                    data: new ValueFieldData({
                        validators: [new RequiredValidator()],
                    }),
                }),
                images: new Field({
                    data: new ArrayOfFieldData(),
                }),
            },
            handleSubmit: this.handleSubmit,
        });
    }

    handleSubmit = ({ data }) => {
        const { images, text } = data;

        emojione.ascii = true;

        return this.thread.createMessage({
            body: emojione.toShort(replace(text)),
            photofileIds: images.map(({ id }) => id),
        });
    };
}

@withDialog
@Styled(defaultStyles)
@observer
class NewMessageView extends React.Component {
    static contextTypes = {
        userStore: PropTypes.object,
        authCheck: PropTypes.func,
    };

    textArea: ?TextAreaView;
    photoFileInput: ?PhotoFileInputView;

    setPhotoFileInput = ref => {
        this.photoFileInput = ref;
    };

    submit = () => {
        const {
            newMessageViewModel: { thread, form },
        } = this.props;
        this.context
            .authCheck(form.submit, AuthOrigin.NewForumMessage)
            .then(message => {
                this.props.newMessageViewModel.setupForm();

                this.props.history.push({
                    pathname: thread.url,
                    search: `?${queryString.stringify({
                        page: 'last',
                        post: message.id,
                    })}`,
                });
            })
            .catch(err => {
                console.error(err);

                this.props.showDialog(
                    new RetryDialog({
                        retry: this.submit,
                        message: err.message,
                    })
                );
            });
    };

    onPhotoFileInputClick = event => {
        if (!this.context.userStore.me) {
            event.preventDefault();
            this.context.authCheck(Promise.resolve, AuthOrigin.NewForumMessage);
        }
    };

    onFocus = () => {
        return this.context.authCheck(Promise.resolve, AuthOrigin.NewForumMessage);
    };

    render() {
        const { newMessageViewModel, styles, focus, onBlur } = this.props;
        const { form } = newMessageViewModel;

        const classNames = [styles.newMessage];

        if (form.field.get('images').data.fields.length) {
            classNames.push(styles.hasUploads);
        }

        return (
            <NewCommentView newCommentViewModel={newMessageViewModel} title={<FormattedMessage id='ADD_GROUP_POST' />}>
                <LoadingWrapperView loading={form.submitting}>
                    <div className={classNames.join(' ')}>
                        <div className={styles.inputs}>
                            <div className={styles.textArea}>
                                <TextAreaView
                                    focus={focus}
                                    field={form.field.get('text')}
                                    emojify={true}
                                    onFocus={this.onFocus}
                                    onBlur={onBlur}
                                    scrollToOnChange={true}
                                />
                            </div>
                        </div>
                        <div className={styles.buttons}>
                            <span className={styles.button}>
                                <SubmitButtonView
                                    icon={<SVGIconView src={newArticle} />}
                                    disabled={!form.valid || form.submitting}
                                    onClick={this.submit}
                                >
                                    <FormattedMessage id='ADD_POST' values={{ gender: true }} />
                                </SubmitButtonView>
                            </span>
                            <span className={styles.photoFileInput}>
                                <PhotoFileInputView
                                    ref={this.setPhotoFileInput}
                                    onClick={this.onPhotoFileInputClick}
                                    field={form.field.get('images')}
                                    fieldDataForFiles={form.photoFileFieldDataForFiles}
                                />
                            </span>
                        </div>
                        <div className={styles.rules}>
                            <div className={styles.uploads}>
                                <FileUploadListView
                                    field={form.field.get('images')}
                                    thumbnail={<FileThumbnailView height={25} width={25} />}
                                />
                            </div>
                        </div>
                    </div>
                </LoadingWrapperView>
            </NewCommentView>
        );
    }
}

export default withRouter(NewMessageView);
